import * as React from "react";
import { ExternalServiceHistoryType } from "../../../generated/nest-graphql";
import { format } from "date-fns";
import {
  formatCarfaxDate,
  formatMiles,
  formatServiceDate,
  isBrakeWork,
  latestBrakeRecordIndex,
  ServiceVisit,
} from "../vehicleHistoryData";
import { usePrefersReducedMotion } from "../../../hooks/usePrefersReducedMotion";
import { RetryButton } from "../RetryButton";
import { JobCard } from "../JobCard";
import { LoadingMessage } from "../LoadingMessage";
import { ServiceSource } from "./serviceHistoryList.utils";
import { SourceToggle } from "./SourceToggle";
import { RecordCard } from "./RecordCard";
import { EmptyList } from "./EmptyList";
import {
  Button,
  ButtonSize,
  DocumentIcon,
  Pressable,
  PullDownIcon,
  RefreshIcon,
  Spinner,
  SpinnerColor,
  Text,
  TextVariant,
} from "../../ui";

export { ServiceSource } from "./serviceHistoryList.utils";
export { SourceToggle } from "./SourceToggle";

// Light enough to stand out from the dark header bar it sits on.
const labelClassName = "text-xs font-semibold rounded px-2 py-1 bg-[#334155] text-[#e2e8f0] shrink-0";

/** Carfax's own lines; older stored records may only have the joined text. */
export const carfaxRecordId = (index: number) => `carfax-record-${index}`;

// Where the header ends once pinned, not where it is now: before the scroll it can sit lower on the page.
const pinnedBottom = (header?: HTMLElement | null): number => {
  if (!header) return 0;
  const style = window.getComputedStyle(header);
  if (style.position !== "sticky") return 0;
  return (parseFloat(style.top) || 0) + header.getBoundingClientRect().height;
};

const carfaxLines = (record: ExternalServiceHistoryType): string[] => {
  const lines = (record?.text ?? []).filter(Boolean) as string[];
  if (lines.length) return lines;
  return [record?.displayText || record?.type || "Service"];
};

export const ServiceHistoryList: React.FC<{
  source: ServiceSource;
  onSourceChange: (next: ServiceSource) => void;
  /** Closed NuBrakes jobs, one card each. */
  services: ServiceVisit[];
  carfax: ExternalServiceHistoryType[];
  carfaxUpdatedAt: Date | null;
  requestedAt?: Date | null;
  refreshing: boolean;
  refreshError?: boolean;
  /** Absent when there is no vehicle record to ask Carfax about. */
  onRefreshCarfax?: () => void;
  /** Shown instead of the Refresh button when it is absent. */
  noRefreshReason?: string;
  /** Off when a parent pins the source toggle with its own header. */
  showSourceToggle?: boolean;
  /** Its card is not a link: the technician is already on that job. */
  currentJobId?: string | null;
  /** Offered beside the reason when the vehicle record failed to load. */
  onRetryVehicle?: () => Promise<unknown>;
  /** The header pinned above the list; a jump lands just below it. */
  pinnedHeaderRef?: React.RefObject<HTMLElement>;
}> = ({
  source,
  onSourceChange,
  services,
  carfax,
  carfaxUpdatedAt,
  requestedAt,
  refreshing,
  refreshError,
  onRefreshCarfax,
  noRefreshReason,
  currentJobId,
  onRetryVehicle,
  pinnedHeaderRef,
  showSourceToggle = true,
}) => {
  const reduceMotion = usePrefersReducedMotion();
  const lastBrakeIndex = React.useMemo(
    () =>
      latestBrakeRecordIndex(
        carfax.map((record) => ({ displayDate: record?.displayDate, lines: carfaxLines(record) }))
      ),
    [carfax]
  );
  const [highlightedIndex, setHighlightedIndex] = React.useState<number | null>(null);
  // The outline marks a position, so a new list must not leave it on a different record.
  React.useEffect(() => setHighlightedIndex(null), [carfax]);
  const jumpToLastBrakeRepair = () => {
    setHighlightedIndex(lastBrakeIndex);
    const target = document.getElementById(carfaxRecordId(lastBrakeIndex));
    if (!target) return;
    target.style.setProperty("scroll-margin-top", `${pinnedBottom(pinnedHeaderRef?.current) + 12}px`);
    target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    // Screen readers follow the jump too.
    target.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });
  };

  return (
    <div className="flex flex-col">
      {showSourceToggle && (
        <SourceToggle
          value={source}
          onChange={onSourceChange}
          counts={{ [ServiceSource.NUBRAKES]: services.length, [ServiceSource.CARFAX]: carfax.length }}
        />
      )}
      <div className="flex flex-col gap-3 p-4">
        {source === ServiceSource.NUBRAKES ? (
          services.length ? (
            <div className="flex flex-col gap-2">
              {services.map((visit, index) => (
                <JobCard
                  key={visit.key}
                  job={visit.job}
                  isCurrent={!!currentJobId && visit.job.id === currentJobId}
                  services={visit.services}
                  odometer={visit.odometer}
                  // Newest first, so the first card is the most recent job.
                  isMostRecent={index === 0}
                />
              ))}
              {/* Open, scheduled and withdrawn jobs are left out on purpose; say so where the list ends. */}
              <Text variant={TextVariant.CAPTION} className="text-gray-400 text-center pt-2">
                Showing closed jobs only
              </Text>
            </div>
          ) : (
            <EmptyList>No closed NuBrakes jobs for this vehicle.</EmptyList>
          )
        ) : (
          <div className="flex flex-col gap-2">
            {/* Phone-first: body-size status and a full-size, 44px-tall Refresh tap target. */}
            {/* Wraps on a narrow screen rather than squeezing the status, link and Refresh. */}
            <div className="flex flex-wrap items-center justify-between gap-x-3">
              {requestedAt ? (
                // Carfax answers in the background, so the wait shows as one.
                <LoadingMessage variant={TextVariant.BODY1} className="min-w-0">
                  Check requested {format(requestedAt, "h:mm a")}
                </LoadingMessage>
              ) : (
                <Text variant={TextVariant.BODY1} className="text-gray-400 min-w-0">
                  {carfaxUpdatedAt ? `Updated ${formatServiceDate(carfaxUpdatedAt)}` : "No records stored"}
                </Text>
              )}
              {lastBrakeIndex >= 0 && (
                // A text link, as it only scrolls; colour inline because the ripple surface's `color: inherit` beats a class.
                <Pressable
                  onClick={jumpToLastBrakeRepair}
                  className="shrink-0 text-sm font-semibold"
                  style={{ minHeight: 44, padding: "0 4px", gap: 4, color: "#60a5fa" }}
                >
                  <span className="underline">Last brake job</span>
                  <PullDownIcon aria-hidden style={{ fontSize: "1em" }} />
                </Pressable>
              )}
              {onRefreshCarfax && (
                <Button
                  startIcon={
                    refreshing ? <Spinner size={18} color={SpinnerColor.INHERIT} aria-hidden /> : <RefreshIcon />
                  }
                  onClick={onRefreshCarfax}
                  // Each tap is a paid Carfax lookup, so none while one is still being answered.
                  disabled={refreshing || !!requestedAt}
                  style={{ textTransform: "none", minHeight: 44, flexShrink: 0 }}
                >
                  {refreshing ? "Checking…" : "Refresh"}
                </Button>
              )}
            </div>
            {refreshError && (
              <Text variant={TextVariant.BODY2} className="text-red-300">
                Carfax could not be reached. Try again in a moment.
              </Text>
            )}
            {carfax.length ? (
              carfax.map((record, index) => (
                <RecordCard
                  key={`${record?.displayDate ?? "record"}-${index}`}
                  title={[formatCarfaxDate(record?.displayDate), formatMiles(record?.odometer)]
                    .filter(Boolean)
                    .join(" · ")}
                  aside={record?.type ? <span className={labelClassName}>{record.type}</span> : null}
                  rows={carfaxLines(record)}
                  RowIcon={DocumentIcon}
                  isFlagged={isBrakeWork}
                  id={carfaxRecordId(index)}
                  highlighted={index === highlightedIndex}
                />
              ))
            ) : (
              <EmptyList>
                {requestedAt
                  ? "Carfax check requested. New records can take a minute."
                  : onRefreshCarfax
                  ? "No Carfax records stored. Refresh to check."
                  : noRefreshReason ?? "No vehicle record to check Carfax with."}
              </EmptyList>
            )}
            {!onRefreshCarfax && onRetryVehicle && <RetryButton size={ButtonSize.SMALL} onRetry={onRetryVehicle} />}
          </div>
        )}
      </div>
    </div>
  );
};
